import { FC, useState } from 'react'
import { SwiperSlide } from 'swiper/react'
import clsx from 'clsx'
import { isVideo } from '@/common/misc'
import PdBreadcrumb from '@/components/pd-breadcrumb/pd-breadcrumb'
import PdMedia from '@/components/pd-media/pd-media'
import PdStandaloneVideo from '@/components/pd-standalone-video/pd-standalone-video'
import PdSwiper from '@/components/pd-swiper/pd-swiper'
import { useMobileDetector } from '@/hooks/use-mobile-detector'
import { CustomerCaseNewInfoData } from '@/service/customer-new.service'
import { useRouter } from 'next/router'

interface Section1Props {
  data?: CustomerCaseNewInfoData['topInfo']
  breadcrumbTitle?: string
}
const Section1: FC<Section1Props> = ({ data, breadcrumbTitle }) => {
  const { title, desc, country, industryName, productName, mediaUrls } = data || {}
  const isMobile = useMobileDetector()
  const [playIcon, setPlayIcon] = useState<string>('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
  const [playingVideoSrc, setPlayingVideoSrc] = useState<string>('')
  const [playing, setPlaying] = useState<boolean>(false)
  const router = useRouter()
  function play(videoSrc: string) {
    setPlayingVideoSrc(videoSrc)
    setPlaying(true)
  }

  return (
    <div className="w-full bg-white">
      <div className="max-w-300 mx-auto pt-6">
        <div className={clsx('w-full flex items-center', 'px-6', 'laptop:px-0')}>
          <PdBreadcrumb
            onlyTopBreadreumb={isMobile}
            linkList={[
              { href: '/case-studies', label: breadcrumbTitle || '' },
              { href: '#', label: title || '' },
            ]}
          />
        </div>
        <div
          className={clsx(
            'flex',
            'flex-col-reverse pt-32px pb-15',
            'laptop:flex-row laptop:justify-between laptop:py-15'
          )}
        >
          <div
            className={clsx(
              'flex-shrink-0 flex-grow-0 flex flex-col',
              'w-full px-6 pt-32px',
              'laptop:w-460px laptop:px-0 laptop:pt-0'
            )}
          >
            <div className={clsx('text-black/90 text-18 font-semibold', 'laptop:text-2xl')}>{title}</div>
            <div className="text-black/60 text-base font-normal leading-normal mt-16px">{desc}</div>
            <div className="text-black/60 text-sm font-normal leading-snug mt-6 flex gap-3 laptop:gap-4 items-center">
              <span>{country}</span>
              {country && industryName && <span className="bg-black/60 w-px h-4" />}
              <span>{industryName}</span>
            </div>
            <div className="flex flex-wrap gap-2 text-black/60 text-sm font-normal leading-snug mt-6">
              {productName?.map((tag) => (
                <span
                  key={tag}
                  className={clsx(
                    'border rounded-sm outline-1 outline-offset-[-1px] outline-[#d7d7d7] inline-flex justify-start items-center',
                    'px-3 py-0.5',
                    'gap-3',
                    'laptop:gap-2'
                  )}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div className={clsx('relative', 'w-full h-266px', 'laptop:w-693px laptop:h-[490px]')}>
            {mediaUrls?.length === 1 && (
              <div className="w-full h-250px laptop:h-462px">
                <PdMedia
                  src={mediaUrls[0]}
                  mask={isVideo(mediaUrls[0])}
                  stopAutoplay={true}
                  object="cover"
                  priority={true}
                >
                  {isVideo(mediaUrls[0]) && (
                    <img
                      src={playIcon}
                      className="absolute block w-12 h-12 transition-all hover:scale-116 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                      alt="play"
                      onMouseEnter={() =>
                        setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play_hover.svg')
                      }
                      onMouseLeave={() =>
                        setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
                      }
                      onClick={() => play(mediaUrls[0])}
                    />
                  )}
                </PdMedia>
              </div>
            )}
            {mediaUrls && mediaUrls.length > 1 && (
              <PdSwiper
                autoplay={true}
                loop={true}
                slidesPerView={1}
                className="flex-1"
                hideNavigation={isMobile}
                paginationOptions={{
                  el: '.pd-pagination-container_black-tab-swiper',
                }}
                navgationOptions={{
                  navigationEl: 'pd-navigation',
                  nextEl: 'pd-navigation-next w-48px h-48px',
                  prevEl: 'pd-navigation-prev w-48px h-48px',
                }}
              >
                {mediaUrls.map((img, index) => (
                  <SwiperSlide key={img + index} className="relative">
                    <div className="w-full h-250px laptop:h-462px">
                      <PdMedia src={img} mask={isVideo(img)} stopAutoplay={true} object="cover" priority={index < 2}>
                        {isVideo(img) && (
                          <img
                            src={playIcon}
                            className="absolute block w-12 h-12 transition-all hover:scale-116 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                            alt="play"
                            onMouseEnter={() =>
                              setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play_hover.svg')
                            }
                            onMouseLeave={() =>
                              setPlayIcon('https://cdn.pudutech.com/official-website/about-us/btn_play.svg')
                            }
                            onClick={() => play(img)}
                          />
                        )}
                      </PdMedia>
                    </div>
                  </SwiperSlide>
                ))}
              </PdSwiper>
            )}
            <div className="absolute left-0 !bottom-0 z-10 pd-pagination-container_black-tab-swiper"></div>
          </div>
        </div>
      </div>
      <PdStandaloneVideo source={playingVideoSrc} show={playing} onClosed={() => setPlaying(false)} />
    </div>
  )
}

export default Section1
